import { Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { tapResponse } from '@ngrx/operators';
import { Observable } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { ERROR, SUCCESS } from 'src/app/global';
import { PatientService } from 'src/app/calvin-emr-shared/services/patient.service';
import { ToastService } from 'src/app/calvin-emr-shared/services/toast.service';
import { Patient } from 'src/app/calvin-emr-shared/models/patient.model';
import { ProviderOrder } from 'src/app/calvin-emr-shared/models/provider-order.model';
import { Vital } from 'src/app/calvin-emr-shared/models/vitals.model';

export interface PatientDashboardState {
  patient: Patient;
  collection: string;
}

@Injectable()
export class PatientDashboardStore extends ComponentStore<PatientDashboardState> {
  constructor(private patientService: PatientService, private toastService: ToastService) {
    super({ patient: new Patient(), collection: '' });
  }

  // Selectors
  readonly patient$: Observable<Patient> = this.select((state) => state.patient);
  readonly providerOrders$: Observable<ProviderOrder[]> = this.select(
    (state) => state.patient.providerOrders
  );
  readonly vitals$: Observable<Vital[]> = this.select((state) => state.patient.vitals);

  // Reducers
  readonly setPatient = this.updater(
    (state: PatientDashboardState, update: PatientDashboardState): PatientDashboardState => ({
      patient: update.patient,
      collection: update.collection,
    })
  );
  readonly setField = this.updater(
    (state: PatientDashboardState, update: { field: string; data: any }): PatientDashboardState => {
      if (update.field in state.patient) {
        state.patient[update.field as keyof Patient] = update.data;
        this.updatePatient({
          patient: state.patient,
          collection: state.collection,
        });
      }
      return state;
    }
  );

  // Effects
  readonly getPatient = this.effect(
    (params$: Observable<{ patientId: string; collection: string }>) => {
      return params$.pipe(
        switchMap((params) =>
          this.patientService.getPatient(params.patientId, params.collection).pipe(
            tapResponse(
              (snapshot) => {
                const patient = this.patientService.getPatientFromSnapshot(snapshot);
                this.setPatient({
                  patient: patient,
                  collection: params.collection,
                });
              },
              (error) => console.error('Unable to retrieve patient:', error)
            )
          )
        )
      );
    }
  );

  readonly updatePatient = this.effect(
    (params$: Observable<{ patient: Patient; collection: string }>) => {
      return params$.pipe(
        switchMap((params) => {
          return this.patientService.upsertPatient(params.patient, params.collection).pipe(
            tapResponse(
              () =>
                this.toastService.addOne({
                  severity: SUCCESS,
                  summary: 'Success',
                  detail: `Updated patient ${params.patient.getLastFirstMiddleName()}!`,
                  life: 2000,
                }),
              (error) =>
                this.toastService.addOne({
                  severity: ERROR,
                  summary: 'Error',
                  detail: `Unable to update patient: ${error}`,
                })
            )
          );
        })
      );
    }
  );
}
